import { useEffect, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { makeStyles } from "tss-react/mui"
import { createTheme, ThemeProvider } from "@mui/material"
import MaterialReactTable, { MaterialReactTableProps } from "material-react-table"
import { getMaterialReactTableTranslation } from "../localizationLib"
import { usePagination } from "../../../shared/hooks/usePagination"
import { selectSelectedLocale } from "../localizationSelectors"
import { StrictOmit } from "../../../lib/lang"

const useStyles = makeStyles()((_theme) => ({
    font: {
        fontSize: "14px",
    },
}))

export type LocMrtProps<TData extends Record<string, any>> = StrictOmit<MaterialReactTableProps<TData>, "localization">
export type LocalizedMRTProps<TData extends Record<string, any>> = {
    managePaginationViaUrl?: boolean
    defaultPageIndex?: number
    defaultPageSize?: number
} & MaterialReactTableProps<TData>

export function LocalizedMaterialReactTable<TData extends Record<string, any> = {}>({
    managePaginationViaUrl = true,
    defaultPageIndex = 0,
    defaultPageSize = 10,
    ...props
}: LocalizedMRTProps<TData>) {
    const selectedLocale = useSelector(selectSelectedLocale)
    const translation = useMemo(() => getMaterialReactTableTranslation(selectedLocale), [selectedLocale])

    const { classes } = useStyles()
    const [pagination, setPagination] = usePagination(managePaginationViaUrl, defaultPageIndex, defaultPageSize)

    const searchInputRef = useRef<HTMLInputElement>(null)

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "/") {
            event.preventDefault()
            const searchButton = document.getElementById("mrt-search-button")

            if (searchButton) {
                ;(searchButton as HTMLElement).click()
                setTimeout(() => {
                    const searchInput = document.querySelector(
                        'input[data-testid="MRT_SearchTextField"]',
                    ) as HTMLInputElement
                    if (searchInput) {
                        searchInput.focus()
                    }
                }, 100)
            }
        }
    }

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown)
        return () => {
            window.removeEventListener("keydown", handleKeyDown)
        }
    }, [])

    return (
        <ThemeProvider theme={createTheme({})}>
            <MaterialReactTable
                {...props}
                muiTableBodyProps={{
                    sx: {
                        fontSize: "14px",
                    },
                    className: classes.font,
                }}
                muiTableProps={{
                    sx: {
                        tableLayout: "fixed",
                    },
                }}
                localization={translation}
                initialState={{ ...props.initialState, pagination }}
                state={{ ...props.state, pagination }}
                onPaginationChange={setPagination}
                muiTablePaginationProps={{
                    rowsPerPageOptions: [10, 50, 100, 1000],
                }}
                muiSearchTextFieldProps={{
                    ...props.muiSearchTextFieldProps,
                    inputRef: searchInputRef,
                }}
            />
        </ThemeProvider>
    )
}
